import React, { Component } from 'react';
import { Modal, Alert } from 'antd';
import { withAppContext } from 'context/AppContext';
import { getCountriesConstant, getStatesConstant } from 'utils/apis/constants';
import { getReservationsById } from 'utils/apis/reservation';
import { calculateTotalAmount, calculateTotalServiceFee } from 'utils/transaction';
import TaxInvoice from 'components/ReceiptTemplate/TaxInvoice';
import Receipt from 'components/ReceiptTemplate/Receipt';
import { guard } from 'utils/general';
import intl from 'react-intl-universal';

/**
 * Invoice PDF modal for Invoice List. Uses the row from the table (`invoice` prop) so
 * `invoiceNo` (reservation running invoice number) and `bookingNo` are shown without a second fetch.
 */
const resolveInvoiceNo = inv => {
  if (!inv) {
    return '';
  }
  const fromRow = inv.invoiceNo != null && inv.invoiceNo !== '' ? inv.invoiceNo : inv.invoiceNumber;
  if (fromRow != null && String(fromRow).trim() !== '') {
    return String(fromRow).trim();
  }
  const res = inv.reservation;
  if (res) {
    const fromRes = res.invoiceNumber != null && res.invoiceNumber !== '' ? res.invoiceNumber : res.invoiceNo;
    if (fromRes != null && String(fromRes).trim() !== '') {
      return String(fromRes).trim();
    }
  }
  return '';
};

const hasListRow = inv => {
  if (!inv) {
    return false;
  }
  if (resolveInvoiceNo(inv)) {
    return true;
  }
  if (inv.bookingNo && String(inv.bookingNo).trim()) {
    return true;
  }
  if (inv._id != null && String(inv._id).length > 0) {
    return true;
  }
  return false;
};

class InvoicePdfModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      countries: [],
      states: [],
      reservation: null,
      isLoadingReservation: false,
      geoConstantsReady: false
    };
    this._pdfPartialKey = null;
    this._pdfPartialData = null;
  }

  componentDidMount = () => {
    this.loadGeoConstants();
    if (this.props.visible) {
      this.loadReservation();
    }
  };

  componentDidUpdate = prevProps => {
    const prevInvoiceId = guard(() => prevProps.invoice._id, '');
    const nextInvoiceId = guard(() => this.props.invoice._id, '');
    if (this.props.visible && (prevInvoiceId !== nextInvoiceId || prevProps.visible !== this.props.visible)) {
      this.loadReservation();
    }
  };

  loadGeoConstants = async () => {
    try {
      const [countries, states] = await Promise.all([getCountriesConstant(), getStatesConstant('MY')]);
      this.setState({
        countries: Object.values(countries || {}),
        states: Object.values(states || {}),
        geoConstantsReady: true
      });
    } catch (e) {
      this.setState({ countries: [], states: [], geoConstantsReady: true });
    }
  };

  loadReservation = async () => {
    const reservationId = guard(() => this.props.invoice.reservation._id, guard(() => this.props.invoice._id, ''));
    if (!reservationId) {
      this.setState({ reservation: null });
      return;
    }

    this.setState({ isLoadingReservation: true });
    const response = await getReservationsById(reservationId, { isWithTransaction: true });
    let reservation = null;
    if (response && response.status === 200 && response.data) {
      const d = response.data;
      reservation = {
        ...d,
        totalPrice: calculateTotalAmount(d.charges, d.taxes),
        totalServiceFee: calculateTotalServiceFee(d.charges)
      };
    }
    this.setState({
      reservation,
      isLoadingReservation: false
    });
  };

  getGuestNationality = nationality => {
    const { countries } = this.state;
    const filteredCountries = countries.filter(country => country.iso3 === nationality || country.iso2 === nationality);
    if (filteredCountries.length > 0) {
      return filteredCountries[0].name;
    }
    return '';
  };

  formatDataForPdf = reservation => {
    const { countries, states } = this.state;
    const taxNo = guard(() => reservation.unit.roomType.property.host.taxNo, '');
    const image = guard(
      () => reservation.unit.roomType.property.host.images[0].imageUrl,
      'https:dashboard-operator-image.s3.amazonaws.com/3386bffc-75c9-4390-a670-fc11ed8febe1_blanksquare.png'
    );
    const city = guard(() => reservation.unit.roomType.property.city, '');
    const street = guard(() => reservation.unit.roomType.property.street, '');
    const zipCode = guard(() => reservation.unit.roomType.property.zipCode, '');
    const user = reservation.createdBy ? reservation.createdBy.userProfile : {};
    let countryCode = guard(() => reservation.unit.roomType.property.countryCode, '');
    countryCode = countryCode ? countries.filter(c => c.iso3 === countryCode || c.iso2 === countryCode)[0] : '';
    const country = countryCode ? countryCode.name : '';
    let state = guard(() => reservation.unit.roomType.property.state, '');
    state = state && states.filter(a => a.code === state)[0] ? states.filter(a => a.code === state)[0].label : '';

    return {
      _id: reservation._id || '',
      confirmationCode: reservation.code || '',
      finePrint: reservation.unit && reservation.unit.finePrint,
      taxNo,
      image,
      city,
      street,
      zipCode,
      createdBy: user,
      country,
      state
    };
  };

  renderPdfBody = ({ partialData, reservation, hasInvoice, host, effectiveCurrency }) => {
    const pdfType = this.props.pdfType || 'invoice';

    if (pdfType === 'receipt') {
      return reservation && partialData ? (
        <Receipt data={partialData} reservation={reservation} getGuestNationality={this.getGuestNationality} currency={effectiveCurrency} />
      ) : hasInvoice ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          {intl.get('invoiceList.pdfModal.receiptPlaceholder').d('Receipt PDF preview will be integrated here.')}
        </div>
      ) : (
        <div style={{ padding: 40, textAlign: 'center' }}>{intl.get('invoiceList.pdfModal.receiptNotFound').d('Receipt not found.')}</div>
      );
    }

    return reservation && partialData ? (
      <TaxInvoice
        data={partialData}
        reservation={reservation}
        getGuestNationality={this.getGuestNationality}
        currency={effectiveCurrency}
        host={host}
      />
    ) : hasInvoice ? (
      <div style={{ padding: 40, textAlign: 'center' }}>
        {intl.get('invoiceList.pdfModal.placeholder').d('Invoice PDF preview will be integrated here.')}
      </div>
    ) : (
      <div style={{ padding: 40, textAlign: 'center' }}>{intl.get('invoiceList.pdfModal.notFound').d('Invoice not found.')}</div>
    );
  };

  render() {
    const { visible, handleClose, invoice, host, pdfType, currency } = this.props;
    const { reservation, isLoadingReservation, geoConstantsReady } = this.state;
    const hasInvoice = hasListRow(invoice) || (reservation && reservation._id);
    const displayInvoiceNo = resolveInvoiceNo(invoice);
    const effectiveCurrency = currency || (reservation && reservation.currency);

    let partialData = null;
    if (reservation && geoConstantsReady) {
      const cacheKey = [
        reservation._id,
        reservation.code,
        reservation.invoiceNumber,
        reservation.totalPrice,
        this.state.countries.length,
        this.state.states.length
      ].join('|');
      if (this._pdfPartialKey !== cacheKey) {
        this._pdfPartialData = this.formatDataForPdf(reservation);
        this._pdfPartialKey = cacheKey;
      }
      partialData = this._pdfPartialData;
    }

    const isReceipt = pdfType === 'receipt';
    const modalTitlePrefix = isReceipt
      ? intl.get('invoiceList.pdfModal.receiptTitle').d('Receipt PDF - ')
      : intl.get('invoiceList.pdfModal.title').d('Invoice PDF - ');

    return (
      <Modal
        title={`${modalTitlePrefix}${displayInvoiceNo}`}
        visible={visible}
        footer={null}
        onCancel={handleClose}
        destroyOnClose
        closable
        width={800}
      >
        <Alert
          message={intl.get('invoiceList.pdfModal.message').d('Info')}
          description={intl.get('invoiceList.pdfModal.desc').d("Please disable Adblocker if the invoice PDF can't be opened.")}
          type="warning"
          showIcon
        />
        <div style={{ marginTop: 16 }}>
          {isLoadingReservation || !geoConstantsReady ? (
            <div style={{ padding: 40, textAlign: 'center' }}>{intl.get('invoiceList.pdfModal.loading').d('Loading...')}</div>
          ) : (
            this.renderPdfBody({ partialData, reservation, hasInvoice, host, effectiveCurrency })
          )}
        </div>
      </Modal>
    );
  }
}

export default withAppContext(InvoicePdfModal);
